import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import AppShareHelper from "src/app/shared/utils/helper";
import {
  getSubscriptionPackage,
  hasPaymentSettingPricing,
  RenewalPricingSource,
  SubscriptionPackage,
  SubscriptionPackageId,
} from "../../../constants/subscription-packages.constant";
import { DigitalMemoryLogService } from "../../../services/digital-memory-log.service";
import {
  CreateSubscriptionResponse,
  OrderService,
  SubscriptionOrder,
  SubscriptionOrderType,
} from "../../../services/order.service";
import { PaymentSettingService } from "../../../services/payment-setting.service";
import { StripeCheckoutService } from "../../../services/stripe-checkout.service";
import { toStripeSmallestUnit } from "../../../utils/stripe-price.util";

@Component({
  selector: "app-preview-stripe-checkout",
  templateUrl: "./preview-stripe-checkout.component.html",
  styleUrls: ["./preview-stripe-checkout.component.scss"],
})
export class PreviewStripeCheckoutComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("cardNumberContainer") cardNumberContainer: ElementRef<HTMLDivElement> | undefined;
  @ViewChild("cardExpiryContainer") cardExpiryContainer: ElementRef<HTMLDivElement> | undefined;
  @ViewChild("cardCvcContainer") cardCvcContainer: ElementRef<HTMLDivElement> | undefined;

  previewId = "";
  previewName = "digital-memory";
  packageId: SubscriptionPackageId = "year";
  selectedPackage: SubscriptionPackage | undefined;
  email = "";
  savedEmail = "";
  loading = false;
  pricingLoading = true;
  sessionLoading = false;
  processing = false;
  paymentReady = false;
  paymentSuccess = false;
  subscriptionReady = false;
  uiPreviewMode = false;
  error: string | null = null;
  paymentError: string | null = null;

  private clientSecret = "";
  private subscriptionId = "";
  private order: SubscriptionOrder | null = null;
  private pricingSource: RenewalPricingSource = {};
  private pricingReady = false;
  private paymentInitialized = false;
  private viewReady = false;
  private mountRetryHandle: any = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private stripeCheckoutService: StripeCheckoutService,
    private digitalMemoryLogService: DigitalMemoryLogService,
    private paymentSettingService: PaymentSettingService,
    private orderService: OrderService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.previewId = params["previewId"] || "";
      this.previewName = params["previewName"] || "digital-memory";
    });

    this.route.queryParams.subscribe(queryParams => {
      const packageId = queryParams["package"];
      if (packageId === "month" || packageId === "year") {
        this.packageId = packageId;
        if (this.pricingReady) {
          this.selectedPackage = getSubscriptionPackage(packageId, this.pricingSource);
        }
      }

      if (queryParams["payment"] === "success" || queryParams["redirect_status"] === "succeeded") {
        this.showThankYou();
        return;
      }
    });

    this.loadRenewalPricing();
  }

  private loadRenewalPricing(): void {
    this.pricingLoading = true;
    this.pricingReady = false;
    this.selectedPackage = undefined;
    this.pricingSource = {};

    if (!this.previewId) {
      this.finishWithoutPricing("Unable to load subscription pricing.");
      return;
    }

    this.digitalMemoryLogService.getPreviewById(this.previewId).subscribe(
      previewData => {
        const data = previewData || {};
        if (hasPaymentSettingPricing(data)) {
          this.applyPricingSource(data);
          return;
        }

        this.loadPaymentSettingFallback(data);
      },
      () => {
        this.loadPaymentSettingFallback({});
      }
    );
  }

  private loadPaymentSettingFallback(previewData: any): void {
    const digitalMemoryLogId = Number((previewData && previewData.id) || this.previewId);

    if (!digitalMemoryLogId || !Number.isFinite(digitalMemoryLogId)) {
      this.finishWithoutPricing("Subscription pricing is not available.");
      return;
    }

    // Public checkout: skip auth interceptors.
    this.paymentSettingService.getByDigitalMemoryLogId(digitalMemoryLogId, false).subscribe(
      setting => {
        if (setting && hasPaymentSettingPricing({ paymentSetting: setting })) {
          this.applyPricingSource(
            Object.assign({}, previewData || {}, {
              paymentSetting: setting,
            })
          );
          return;
        }

        this.finishWithoutPricing("Subscription pricing is not available.");
      },
      () => {
        this.finishWithoutPricing("Unable to load subscription pricing.");
      }
    );
  }

  private applyPricingSource(source: RenewalPricingSource): void {
    this.pricingSource = source || {};
    this.selectedPackage = getSubscriptionPackage(this.packageId, this.pricingSource);
    this.pricingReady = !!this.selectedPackage;
    this.pricingLoading = false;

    if (!this.selectedPackage) {
      this.error = "Subscription pricing is not available.";
      this.changeDetectorRef.detectChanges();
      return;
    }

    if (!this.stripeCheckoutService.getPublishableKey()) {
      this.uiPreviewMode = true;
    }

    this.changeDetectorRef.detectChanges();
  }

  private finishWithoutPricing(message: string): void {
    this.pricingSource = {};
    this.selectedPackage = undefined;
    this.pricingReady = false;
    this.pricingLoading = false;
    this.error = message;
    this.changeDetectorRef.detectChanges();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    if (this.subscriptionReady) {
      this.tryMountPaymentForm();
    }
  }

  ngOnDestroy(): void {
    this.clearMountRetry();
    this.stripeCheckoutService.destroyElements();
  }

  async onContinueWithEmail(): Promise<void> {
    if (this.sessionLoading || this.processing || this.paymentSuccess) {
      return;
    }

    const trimmedEmail = (this.email || "").trim();
    if (!trimmedEmail) {
      this.paymentError = "Please enter your email address.";
      return;
    }

    if (!this.isValidEmail(trimmedEmail)) {
      this.paymentError = "Please enter a valid email address.";
      return;
    }

    const digitalMemoryLogId = Number(this.previewId);
    if (!digitalMemoryLogId || !Number.isFinite(digitalMemoryLogId)) {
      this.paymentError = "Digital Memory Log id is invalid.";
      return;
    }

    if (!this.selectedPackage || !(this.selectedPackage.price > 0)) {
      this.paymentError = "Subscription pricing is not available.";
      return;
    }

    this.savedEmail = trimmedEmail.toLowerCase();
    this.email = trimmedEmail;
    this.sessionLoading = true;
    this.paymentError = null;
    this.error = null;

    if (this.uiPreviewMode) {
      this.subscriptionReady = true;
      this.sessionLoading = false;
      this.paymentReady = false;
      this.changeDetectorRef.detectChanges();
      return;
    }

    try {
      const session = await this.stripeCheckoutService
        .createSubscription({
          email: trimmedEmail,
          digitalMemoryLogId: digitalMemoryLogId,
          type: this.mapPackageIdToOrderType(this.packageId),
          price: toStripeSmallestUnit(this.selectedPackage.price, this.selectedPackage.currency),
          currency: (this.selectedPackage.currency || "").trim().toLowerCase(),
        })
        .toPromise();

      if (session && session.url) {
        window.location.href = session.url;
        return;
      }

      this.applySubscriptionSession(session);

      if (!this.clientSecret) {
        this.paymentError = "Payment session is missing client secret.";
        this.sessionLoading = false;
        this.subscriptionReady = false;
        return;
      }

      this.subscriptionReady = true;
      this.sessionLoading = false;
      this.changeDetectorRef.detectChanges();
      this.tryMountPaymentForm();
    } catch (sessionError) {
      this.sessionLoading = false;
      this.subscriptionReady = false;
      this.paymentError = AppShareHelper.extractHttpErrorMessage(sessionError, "Unable to create payment session.");
      this.changeDetectorRef.detectChanges();
    }
  }

  retry(): void {
    this.clearMountRetry();
    this.error = null;
    this.paymentError = null;
    this.loading = false;
    this.pricingLoading = true;
    this.sessionLoading = false;
    this.paymentReady = false;
    this.paymentSuccess = false;
    this.subscriptionReady = false;
    this.uiPreviewMode = false;
    this.paymentInitialized = false;
    this.pricingReady = false;
    this.selectedPackage = undefined;
    this.clientSecret = "";
    this.subscriptionId = "";
    this.order = null;
    this.savedEmail = "";
    this.stripeCheckoutService.destroyElements();
    this.loadRenewalPricing();
  }

  goBack(): void {
    if (!this.previewId) {
      this.router.navigate(["/"]);
      return;
    }

    this.router.navigate([`preview-${this.previewName}-${this.previewId}`, "subscription"], {
      queryParams: { package: this.packageId },
    });
  }

  goToVerifyAccess(): void {
    if (!this.previewId) {
      this.router.navigate(["/"]);
      return;
    }

    const queryParams: { [key: string]: string } = {};
    const email = (this.savedEmail || this.email || "").trim();
    if (email) {
      queryParams.email = email;
    }

    this.router.navigate([`preview-${this.previewName}-${this.previewId}`, "access"], {
      queryParams: queryParams,
    });
  }

  async onSubmitPayment(): Promise<void> {
    if (this.uiPreviewMode) {
      this.showThankYou();
      return;
    }

    if (this.processing || !this.paymentReady || !this.subscriptionReady) {
      return;
    }

    if (!this.clientSecret) {
      this.paymentError = "Payment session is missing client secret.";
      return;
    }

    const emailForPayment = this.savedEmail || (this.email || "").trim();
    if (!emailForPayment) {
      this.paymentError = "Please enter your email address.";
      return;
    }

    this.processing = true;
    this.paymentError = null;

    try {
      const result = await this.stripeCheckoutService.confirmCardPayment(
        this.clientSecret,
        emailForPayment,
        this.buildReturnUrl()
      );

      if (result && result.error) {
        this.paymentError = result.error.message || "Payment could not be completed.";
        this.processing = false;
        return;
      }

      const paymentIntent = (result as any) && (result as any).paymentIntent;
      if (paymentIntent && paymentIntent.status === "succeeded") {
        this.showThankYou();
        return;
      }

      // 3DS / redirect flows leave this page; otherwise keep waiting state brief.
      this.processing = false;
    } catch (submitError) {
      this.paymentError = AppShareHelper.extractHttpErrorMessage(submitError, "Unable to process payment.");
      this.processing = false;
    }
  }

  private applySubscriptionSession(session: CreateSubscriptionResponse | null | undefined): void {
    this.clientSecret = this.orderService.extractClientSecret(session);
    this.subscriptionId = (session && (session.subscriptionId || session.subscription_id)) || "";
    this.order = (session && session.order) || null;

    if (!this.subscriptionId && this.order && this.order.stripeSubscriptionId) {
      this.subscriptionId = this.order.stripeSubscriptionId;
    }
  }

  private showThankYou(): void {
    this.paymentSuccess = true;
    this.loading = false;
    this.sessionLoading = false;
    this.processing = false;
    this.paymentReady = false;
    this.subscriptionReady = false;
    this.error = null;
    this.paymentError = null;
    this.stripeCheckoutService.destroyElements();
  }

  private tryMountPaymentForm(): void {
    if (
      this.paymentSuccess ||
      !this.viewReady ||
      this.paymentInitialized ||
      !this.previewId ||
      !this.pricingReady ||
      !this.selectedPackage ||
      !this.subscriptionReady ||
      this.uiPreviewMode
    ) {
      return;
    }

    // Force *ngIf containers into the DOM before reading ViewChild (Angular 7).
    this.changeDetectorRef.detectChanges();
    this.initializePaymentForm();
  }

  private initializePaymentForm(retryCount = 0): void {
    const cardNumberContainer = this.cardNumberContainer && this.cardNumberContainer.nativeElement;
    const cardExpiryContainer = this.cardExpiryContainer && this.cardExpiryContainer.nativeElement;
    const cardCvcContainer = this.cardCvcContainer && this.cardCvcContainer.nativeElement;

    if (!cardNumberContainer || !cardExpiryContainer || !cardCvcContainer) {
      if (retryCount < 10) {
        this.clearMountRetry();
        this.mountRetryHandle = setTimeout(() => this.initializePaymentForm(retryCount + 1), 50);
        return;
      }

      this.loading = false;
      this.error = "Unable to load payment form containers.";
      return;
    }

    this.clearMountRetry();
    this.paymentInitialized = true;
    this.loading = true;
    this.error = null;
    this.paymentError = null;
    this.paymentReady = false;

    this.stripeCheckoutService
      .mountCardElements({
        cardNumberContainer,
        cardExpiryContainer,
        cardCvcContainer,
      })
      .then(() => {
        this.loading = false;
        this.paymentReady = true;
        this.changeDetectorRef.detectChanges();
      })
      .catch(mountError => {
        this.loading = false;
        this.paymentInitialized = false;
        this.error = AppShareHelper.extractHttpErrorMessage(mountError, "Unable to load payment form.");
        this.changeDetectorRef.detectChanges();
      });
  }

  private mapPackageIdToOrderType(packageId: SubscriptionPackageId): SubscriptionOrderType {
    return packageId === "month" ? "monthly" : "annual";
  }

  private isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  private clearMountRetry(): void {
    if (this.mountRetryHandle) {
      clearTimeout(this.mountRetryHandle);
      this.mountRetryHandle = null;
    }
  }

  private buildReturnUrl(): string {
    const origin = window.location.origin.replace(/\/$/, "");
    return `${origin}/preview-${this.previewName}-${this.previewId}/subscription/checkout?package=${this.packageId}&payment=success`;
  }
}
